import { useCallback, useEffect, useRef } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { TableCell, TableRow as MuiTableRow, Tooltip } from '@mui/material'

import { trim } from '@components/CommandParameters/helpers/isConfiguredParameter'

import { AutocompleteOption } from '@types'

import { BindTypeCell } from './cells/BindTypeCell'
import { ClearCell } from './cells/ClearCell'
import { ParameterCell } from './cells/ParameterCell'
import { ValueCell } from './cells/ValueCell'
import { CommandConfigRowData } from './CommandConfigTable'
import { TOOLTIP_SLOT_PROPS } from './consts'

type TableRowProps = {
  row: CommandConfigRowData
  index: number
  namePrefix: string
  isDisabled?: boolean
  typeOptions: AutocompleteOption[]
  defaultRefFieldsObjectOption: AutocompleteOption<string> | null
  refObjectOptions?: AutocompleteOption[]
  isChangeRefObjectAllowed?: boolean
  onEditJs?: (index: number) => void
  isHidden?: boolean
}

export const TableRow = ({
  row,
  index,
  namePrefix,
  isDisabled,
  typeOptions,
  defaultRefFieldsObjectOption,
  refObjectOptions,
  isChangeRefObjectAllowed,
  onEditJs,
  isHidden,
}: TableRowProps) => {
  const { control, setValue } = useFormContext()

  const prevTypeIdRef = useRef<string | null>(null)

  const rowPath = `${namePrefix}.${index}` as const
  const typePath = `${namePrefix}.${index}.type` as const
  const valuePath = `${namePrefix}.${index}.value` as const
  const pathArrayPath = `${namePrefix}.${index}.pathArray` as const

  const current = useWatch({ control, name: rowPath as any })
  const type = current?.type ?? null
  const typeId = type?.id ?? null

  const hasRowData = Boolean(
    current?.type ||
      trim(current?.value) ||
      (current?.pathArray?.length ?? 0) > 0 ||
      current?.objectCode
  )

  const patchRow = useCallback(
    (patch: Record<string, unknown>) => {
      const prev = (current ?? {}) as any

      setValue(rowPath as any, { ...prev, ...patch }, { shouldDirty: true, shouldValidate: true })
    },
    [current, rowPath, setValue]
  )

  const handleClearRow = useCallback(() => {
    patchRow({ type: null, value: '', pathArray: null, objectCode: null })
  }, [patchRow])

  const handleClearValueCell = useCallback(() => {
    patchRow({ value: '', pathArray: null, objectCode: null })
  }, [patchRow])

  const handleChangeRootObject = useCallback(
    (value: AutocompleteOption<string>) => {
      setValue(`${namePrefix}.${index}.objectCode` as any, value, {
        shouldDirty: true,
        shouldValidate: false,
      })
    },
    [index, namePrefix, setValue]
  )

  const handleEditJs = useCallback(() => {
    onEditJs?.(index)
  }, [onEditJs, index])

  useEffect(() => {
    const nextTypeId = typeId == null ? null : String(typeId).toUpperCase()
    const prevTypeId = prevTypeIdRef.current

    if (prevTypeId === null) {
      prevTypeIdRef.current = nextTypeId

      return
    }
    if (prevTypeId === nextTypeId) return

    handleClearValueCell()

    prevTypeIdRef.current = nextTypeId
  }, [typeId, handleClearValueCell])

  return (
    <MuiTableRow hover data-row-index={index} sx={isHidden ? { display: 'none' } : undefined}>
      <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        <ParameterCell row={row} />
      </TableCell>

      <TableCell>
        <BindTypeCell
          fieldName={typePath}
          isDisabled={isDisabled}
          isRequired={row.isRequired === true}
          options={typeOptions}
        />
      </TableCell>

      <TableCell>
        <ValueCell
          defaultRefFieldsObjectOption={current?.objectCode || defaultRefFieldsObjectOption}
          isChangeRefObjectAllowed={Boolean(isChangeRefObjectAllowed)}
          isDisabled={isDisabled}
          isRequired={row.isRequired === true}
          isTypeSelected={Boolean(type)}
          pathArrayFieldName={pathArrayPath as any}
          refObjectOptions={refObjectOptions ?? []}
          typeId={typeId as any}
          valueFieldName={valuePath as any}
          onChangeRootObject={handleChangeRootObject}
          onEditJs={handleEditJs}
        />
      </TableCell>

      <TableCell
        sx={{
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        <Tooltip
          arrow
          disableHoverListener={row.valueTypeCode == null}
          placement='bottom'
          slotProps={TOOLTIP_SLOT_PROPS}
          title={row.valueTypeCode}
        >
          <span>{row.valueTypeCode ?? '—'}</span>
        </Tooltip>
      </TableCell>

      <TableCell
        title={row.defaultValue == null ? '' : String(row.defaultValue)}
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        <Tooltip
          arrow
          disableHoverListener={row.defaultValue == null}
          placement='bottom'
          slotProps={TOOLTIP_SLOT_PROPS}
          title={row.defaultValue == null ? '' : String(row.defaultValue)}
        >
          <span>
            {row.defaultValue == null
              ? ''
              : typeof row.defaultValue === 'string'
              ? row.defaultValue
              : JSON.stringify(row.defaultValue)}
          </span>
        </Tooltip>
      </TableCell>

      <TableCell align='center'>
        <ClearCell hasRowData={hasRowData} isDisabled={isDisabled} onClearRow={handleClearRow} />
      </TableCell>
    </MuiTableRow>
  )
}
